import type { GridColumn } from '@/components/DataGrid/PlatformGrid.types'

export interface SduiGridConfig {
    endpoint: string
    method?: 'GET' | 'POST'
    /** Request body sent for POST endpoints — shape varies per endpoint */
    defaultRequest?: Record<string, unknown>
    /**
     * Name of the request-body param that should receive the runtime DTS id.
     * The value is filled from the screen's `dtsId` at fetch time. Set this per
     * grid since the param name varies (e.g. 'StartedDtsId' here, another key
     * elsewhere). Omit it for grids that don't need the DTS context.
     */
    dtsIdParam?: string
    /**
     * Row property used as the stable row identity for selection (checkbox
     * multi-select, row highlighting). Defaults to the grid's OBJName, which is
     * not a real row field — set this (e.g. 'licId') so selections key off a
     * stable id rather than a JSON-stringified whole-row fallback.
     */
    idField?: string
    /**
     * Map of request-body param name → property on the selected row to source
     * its value from (e.g. { SDId: 'sdId', LicTypeId: 'licTypeId' }). Filled at
     * fetch time from the nearest ancestor's selected row. Omit if not needed.
     */
    rowParams?: Record<string, string>
    /**
     * Row property used as this grid's human-readable label (e.g. in the
     * breadcrumb's "selected data" segment). Defaults to the first column's
     * `key` when omitted. Set it when the first column isn't the row's identity.
     */
    labelField?: string
    columns?: GridColumn<Record<string, unknown>>[]
    title?: string
    defaultPageSize?: number
    enableFilters?: boolean
    enableExport?: boolean
    /**
     * Key/value pairs applied client-side after fetch. Rows that don't match
     * every entry are removed before the grid renders. Use for fields the server
     * doesn't filter reliably (e.g. soft-delete flags).
     */
    clientFilter?: Record<string, unknown>
    /**
     * Column keys that should be hidden by default on initial load. The user
     * can still re-enable them via the Columns panel. Only applies to
     * response-driven columns (when `columns` is omitted).
     */
    defaultHiddenColumns?: string[]
    /**
     * Initial pixel widths for response-driven columns, keyed by column key.
     * Only applies when `columns` is omitted. Example: { contractName: 220 }
     */
    columnWidths?: Record<string, number>
}

/**
 * Shared config for the license-management list grids. Several OBJNames map to
 * this same config (they hit the same generic endpoint, scoped by StartedDtsId).
 */
const LICENSE_LIST_GRID_CONFIG: SduiGridConfig = {
    endpoint: '/api/license-mgt/grid',
    method: 'POST',
    defaultRequest: {
        GridRequest: {
            page: 1,
            pageSize: 20,
            sortDir: 'asc',
        },
    },
    dtsIdParam: 'StartedDtsId',
    idField: 'licId',
    // Scope a child list to the upstream-selected parent (e.g. the Distributor
    // list shows only distributors under the selected Admin). Dropped at the top
    // level (Admin list) where there is no ancestor row → backend returns all.
    rowParams: { ParentLicId: 'licId' },
    labelField: 'licName',
    columns: [
        { key: 'licName', label: 'Name', width: 220, sortable: true },
        {
            key: 'userName',
            label: 'User Account',
            width: 200,
            sortable: true,
        },
    ],

    defaultPageSize: 5,
    enableFilters: true,
    enableExport: true,
}

/**
 * Admin user-list grid — its own endpoint, scoped by the current tab's
 * StartedDtsId plus the upstream-selected admin row's sdId / licTypeId.
 */
const ADMIN_USER_LIST_GRID_CONFIG: SduiGridConfig = {
    endpoint: '/api/license-mgt/users/grid',
    method: 'POST',
    defaultRequest: {
        GridRequest: {
            page: 1,
            pageSize: 20,
            sortDir: 'asc',
        },
        isDeleted: false,
    },
    clientFilter: { isDeleted: false },
    idField: 'userId',
    rowParams: { licId: 'licId', licType: 'licTypeId' },
    labelField: 'userName',
    columns: [
        {
            key: 'userId',
            label: 'User Id',
            width: 400,
            sortable: true,
        },
        {
            key: 'userName',
            label: 'User Account',
            width: 400,
            sortable: true,
        },
    ],
    defaultPageSize: 5,
    enableFilters: true,
    enableExport: true,
}

/**
 * Admin license-list grid — its own endpoint, scoped by the upstream-selected
 * row's licId / licType. Columns omitted → response-driven (default grid).
 */
const ADMIN_LICENSE_LIST_GRID_CONFIG: SduiGridConfig = {
    endpoint: '/api/license-mgt/licenses/grid',
    method: 'POST',
    defaultRequest: {
        GridRequest: {
            page: 1,
            pageSize: 20,
            sortDir: 'asc',
        },
    },
    // request key ← selected-row property (row has `licId` + `licTypeId`)
    rowParams: { licId: 'licId', licType: 'licTypeId' },
    idField: 'contractId',
    // License-grid rows are contracts → label the breadcrumb's selected-data
    // segment with the contract name rather than the licensee/org name.
    labelField: 'contractName', // columns omitted → response-driven (visibleColumns/derived); add a fixed list later
    defaultHiddenColumns: [
        'licId',
        'licensorId',
        'licensorName',
        'contractId',
        'licenseeId',
    ],
    columnWidths: {
        contractName: 280,
        startDate: 160,
        endDate: 160,
        licenseeId: 100,
        licenseeName: 280,
    },
    defaultPageSize: 5,
    enableFilters: true,
    enableExport: true,
}

const BA_LIST_GRID_CONFIG: SduiGridConfig = {
    endpoint: '/api/businessAdministration/grid',
    method: 'POST',
    defaultRequest: {
        GridRequest: {
            page: 1,
            pageSize: 20,
            sortDir: 'asc',
        },
    },
    rowParams: { licId: 'licId' },
    idField: 'businessAdministrationId',
    defaultPageSize: 5,
    enableFilters: true,
    enableExport: true,
}

const BA_USER_LIST_GRID_CONFIG: SduiGridConfig = {
    endpoint: '/api/businessAdministration/users/grid',
    method: 'POST',
    defaultRequest: {
        GridRequest: {
            page: 1,
            pageSize: 20,
            sortDir: 'asc',
        },
    },
    rowParams: { baId: 'businessAdministrationId', licId: 'licId' },
    idField: 'userId',
    labelField: 'userName',
    columns: [
        {
            key: 'userId',
            label: 'User Id',
            width: 400,
            sortable: true,
        },
        {
            key: 'userName',
            label: 'User Name',
            width: 400,
            sortable: true,
        },
    ],
    defaultPageSize: 5,
    enableFilters: true,
    enableExport: true,
}
// const BA_LIST_GRID_CONFIG: SduiGridConfig = {
//     endpoint: '/api/businessAdministration/grid',
//     method: 'POST',
//     defaultRequest: {
//         GridRequest: {
//             page: 1,
//             pageSize: 20,
//             sortDir: 'asc',
//         },
//     },
//     // request key ← selected-row property (row has `licId` + `licTypeId`)
//     rowParams: { licId: 'licId' },
//     //labelField: 'licName',
//     // columns omitted → response-driven (visibleColumns/derived); add a fixed list later
//     defaultPageSize: 5,
//     enableFilters: true,
//     enableExport: true,
// }

/**
 * Maps OBJName values from the backend to their grid configuration.
 * Add an entry here for each named grid used on SDUI screens.
 */
export const SDUI_GRID_CONFIG: Record<string, SduiGridConfig> = {
    //Admin Visible Grids
    LIC_Admin_List_Grid_1: LICENSE_LIST_GRID_CONFIG,
    LIC_Admin_User_List_Grid_1: ADMIN_USER_LIST_GRID_CONFIG,
    LIC_Distributor_List_Grid_1: LICENSE_LIST_GRID_CONFIG,
    LIC_Distributor_User_List_Grid_1: ADMIN_USER_LIST_GRID_CONFIG,
    LIC_Admin_License_List_Grid_1: ADMIN_LICENSE_LIST_GRID_CONFIG,

    //Distributor Visible Grids
    LIC_Partner_User_List_Grid_1: ADMIN_USER_LIST_GRID_CONFIG,
    LIC_Partner_List_Grid_1: LICENSE_LIST_GRID_CONFIG,
    LIC_Distributor_License_List_Grid_1: ADMIN_LICENSE_LIST_GRID_CONFIG,
    LIC_Partner_License_List_Grid_1: ADMIN_LICENSE_LIST_GRID_CONFIG,

    //Partner Visible Grids
    LIC_Client_List_Grid_1: LICENSE_LIST_GRID_CONFIG,
    LIC_Client_User_List_Grid_1: ADMIN_USER_LIST_GRID_CONFIG,
    LIC_Client_License_List_Grid_1: ADMIN_LICENSE_LIST_GRID_CONFIG,
    LIC_BA_List_Grid_1: BA_LIST_GRID_CONFIG,
    LIC_BA_User_List_Grid_1: BA_USER_LIST_GRID_CONFIG,
    //LIC_Admin_License_List_Grid_1: ADMIN_LICENSE_LIST_GRID_CONFIG,
}

/**
 * Candidate row properties to fall back to when a grid config doesn't resolve a
 * usable label (no `labelField`, no columns, or the configured field is empty).
 * Ordered most- to least-specific so a selected row always renders *something*
 * meaningful rather than "[No data selected]".
 */
const ROW_LABEL_FALLBACKS = [
    'licName',
    'userName',
    'licensorName',
    'licenseeName',
    'sdTypeName',
    'name',
    'objToken',
]

const toNonEmptyString = (value: unknown): string | null => {
    if (value == null) return null
    const str = String(value).trim()
    return str === '' ? null : str
}

/**
 * Resolve a human-readable label for a selected grid row. Precedence:
 *   1. the source grid's `labelField` (or its first column key),
 *   2. the first non-empty value among ROW_LABEL_FALLBACKS,
 * returning a trimmed string or `null` when nothing usable is found.
 *
 * `sourceObjName` is the OBJName of the grid that produced the row (persisted
 * alongside the selection); it selects which grid config supplies the field.
 */
export const getGridRowLabel = (
    row: Record<string, unknown> | null | undefined,
    sourceObjName?: string,
): string | null => {
    if (!row) return null

    const config = sourceObjName ? SDUI_GRID_CONFIG[sourceObjName] : undefined
    const primaryField = config?.labelField ?? config?.columns?.[0]?.key
    const primary = primaryField ? toNonEmptyString(row[primaryField]) : null
    if (primary) return primary

    for (const field of ROW_LABEL_FALLBACKS) {
        const fallback = toNonEmptyString(row[field])
        if (fallback) return fallback
    }
    return null
}
